<template>
  <v-dialog v-model="show" v-bind="win.dialogProps" persistent>
    <v-card rounded="lg" class="cw-window" :ref="win.setCardEl">
      <!-- Header (zugleich Drag-Griff; Doppelklick maximiert/stellt wieder her) -->
      <header
        class="cw-head dialog-window-handle"
        @pointerdown="win.onHandlePointerDown"
        @dblclick="win.onHandleDblClick"
      >
        <MessageCircle :size="16" class="cw-head-icon" />
        <div class="cw-head-titles">
          <span class="cw-head-subject">{{ subject }}</span>
          <span v-if="conversation?.caseNumber" class="cw-head-case">{{ conversation.caseNumber }}</span>
        </div>
        <!-- Erklär-Einblendungen wie im Chat-Verlauf (Angleichung 03.10.2026). -->
        <div class="cw-head-actions">
          <ChatTip :title="$gt('Minimieren')" :text="$gt('Legt das Fenster in der Leiste am unteren Rand ab; die Unterhaltung bleibt geöffnet.')">
            <button
              type="button"
              class="cw-head-btn"
              :aria-label="$gt('Minimieren')"
              @click="win.minimize"
            >
              <Minus :size="16" />
            </button>
          </ChatTip>
          <ChatTip
            :title="win.isMaximized ? $gt('Wiederherstellen') : $gt('Maximieren')"
            :text="win.isMaximized ? $gt('Stellt die vorherige Fenstergröße wieder her.') : $gt('Vergrößert das Fenster auf die ganze Arbeitsfläche. Doppelklick auf die Titelzeile bewirkt dasselbe.')"
          >
            <button
              type="button"
              class="cw-head-btn"
              :aria-label="win.isMaximized ? $gt('Wiederherstellen') : $gt('Maximieren')"
              @click="win.toggleMaximize"
            >
              <Minimize2 v-if="win.isMaximized" :size="14" />
              <Maximize2 v-else :size="14" />
            </button>
          </ChatTip>
          <ChatTip :title="$gt('Schließen')" :text="$gt('Schließt das Fenster. Die Unterhaltung bleibt im E-Mail-Verlauf erhalten.')">
            <button
              type="button"
              class="cw-head-btn"
              :aria-label="$gt('Schließen')"
              @click="close"
            >
              <X :size="16" />
            </button>
          </ChatTip>
        </div>
      </header>

      <!-- Nachrichtenverlauf: dieselbe Komponente wie im Postfach, damit
           das schwebende Fenster nicht eigene Regeln hat (Feedback 14.09.26). -->
      <div class="cw-body" ref="scrollEl">
        <MessageThread
          :messages="messages"
          :loading="loading"
          :empty-label="$gt('Noch keine Nachrichten in dieser Konversation')"
          classifiable
          @classify="classifyTarget = $event"
        />
      </div>

      <!-- Antwort: derselbe Editor und dieselbe Werkzeugleiste wie in
           „Neue Nachricht“ und im Antwortfeld der Korrespondenz
           (Kundenfeedback 25.10.2026 Teil 3, I). Versand läuft über denselben
           Endpunkt wie im MessageCenter — E-Mail-Zustellung an externe
           Empfänger erfolgt dadurch identisch. -->
      <footer class="cw-foot">
        <div v-if="externalEmails.length" class="cw-ext" :title="externalEmails.join(', ')">
          <AtSign :size="11" />
          <span class="cw-ext__text">
            {{ $gt('Antwort geht per E-Mail an') }}: {{ externalEmails.join(', ') }}
          </span>
        </div>
        <ReplyComposer
          ref="replyComposer"
          v-model="replyHtml"
          v-model:documents="pendingDocuments"
          v-model:files="pendingAttachments"
          class="cw-composer"
          :conversation="conversation"
          :placeholder="$gt('Antwort schreiben…')"
          @send="sendReply"
        >
          <template #actions>
            <router-link
              v-if="caseRoute"
              class="cw-case-link"
              :to="caseRoute"
              :title="$gt('Zum Fall (E-Mails)')"
              @click="close"
            >
              <FolderOpen :size="12" />{{ $gt('Im Fall öffnen') }}
            </router-link>
            <button
              type="button"
              class="cw-send"
              :disabled="!canSend || sending"
              @click="sendReply"
            >
              <Send :size="13" />{{ sending ? $gt('Wird gesendet…') : $gt('Senden') }}
            </button>
          </template>
        </ReplyComposer>
      </footer>

      <!-- Absender & Richtung festlegen – derselbe Dialog wie im Chat-Verlauf. -->
      <DirectionDialog
        :busy="classifying"
        :message="classifyTarget"
        :subject="subject"
        @apply="applyClassification"
        @close="classifyTarget = null"
      />

      <!-- Resize-Handles an Kanten und Ecken -->
      <div
        v-for="dir in resizeDirs"
        :key="dir"
        class="cw-resize"
        :class="'cw-resize--' + dir"
        @pointerdown="win.onResizePointerDown($event, dir)"
      />
    </v-card>
  </v-dialog>
</template>

<script>
import { toRef } from "vue";
import { API_BASE_URL } from "@/config";
import { useAuthStore } from "@/stores/authStore.ts";
import { useSnackbarStore } from "@/stores/snackbarStore.ts";
import { useDialogWindow } from "@/composables/useDialogWindow";
import ReplyComposer from "@/components/messages/compose/ReplyComposer.vue";
import { ownText } from "@/components/messages/compose/composeBody";
import MessageThread from "@/components/messages/MessageThread.vue";
import ChatTip from "@/components/messages/chat/ChatTip.vue";
import DirectionDialog from "@/components/messages/chat/DirectionDialog.vue";
import { attachCaseDocuments, setMessageDirection } from "@/components/messages/chat/chatApi";
import MessageReceipts from "@/components/messages/MessageReceipts.vue";
import {
  MessageCircle, Minus, Maximize2, Minimize2, X, Send, FolderOpen, AtSign,
} from "lucide-vue-next";

// Feedback 29.08.26, III: Das Fenster hält den Verlauf aktuell, während der
// Anwender im Fall weiterarbeitet.
const REFRESH_INTERVAL_MS = 30_000;

/**
 * Schwebendes, verschieb-/minimier-/skalierbares Unterhaltungs-Fenster
 * (Kundenfeedback 29.08.26, III): Eine Konversation aus dem MessageCenter
 * kann "als Fenster gelöst" werden, bleibt beim Arbeiten im Fall sichtbar
 * und erlaubt direkte Text-Antworten. Anhänge u. ä. weiterhin über die
 * Fallakte ("Im Fall öffnen").
 */
export default {
  name: "ConversationWindow",

  components: {
    ReplyComposer, MessageReceipts, MessageThread, ChatTip, DirectionDialog,
    MessageCircle, Minus, Maximize2, Minimize2, X, Send, FolderOpen, AtSign,
  },

  props: {
    modelValue: { type: Boolean, default: false },
    /** Konversation aus dem MessageCenter (id, idCase, subject, caseNumber). */
    conversation: { type: Object, default: null },
  },

  emits: ["update:modelValue", "sent"],

  setup(props) {
    const win = useDialogWindow({
      id: "conversation-window",
      title: "Unterhaltung",
      icon: MessageCircle,
      isOpen: toRef(props, "modelValue"),
      // Kundenfeedback 30.08.26: Standardansicht deutlich kompakter — ein
      // schmales Seitenfenster rechts unten statt eines seitenfüllenden
      // Dialogs. Drag/Resize/Maximieren bleiben unverändert möglich.
      resizable: {
        minWidth: 380,
        minHeight: 340,
        defaultWidth: "min(460px, 92vw)",
        defaultHeight: "min(60vh, 640px)",
        defaultPlacement: "bottom-right",
      },
    });
    return { win };
  },

  data() {
    return {
      messages: [],
      loading: false,
      replyHtml: "",
      pendingAttachments: [],
      // Dokumente aus der Akte (Referenzen; das Backend kopiert sie beim Senden).
      pendingDocuments: [],
      sending: false,
      /** E-Mail, deren Absender/Richtung gerade festgelegt wird. */
      classifyTarget: null,
      classifying: false,
      refreshTimer: null,
      resizeDirs: ["n", "s", "e", "w", "ne", "nw", "se", "sw"],
    };
  },

  computed: {
    show: {
      get() {
        return this.modelValue;
      },
      set(value) {
        this.$emit("update:modelValue", value);
      },
    },

    subject() {
      return this.conversation?.subject || this.$gt("(Kein Betreff)");
    },

    /**
     * Sendbar erst mit eigenem Text: die vorbelegte Signatur zählt nicht mit
     * (Kundenfeedback 14.09.26).
     */
    canSend() {
      return ownText(this.replyHtml).length > 0
        || this.pendingAttachments.length > 0
        || this.pendingDocuments.length > 0;
    },

    /**
     * Externe E-Mail-Empfänger der Konversation: Antworten werden vom
     * Backend automatisch per E-Mail an sie zugestellt (identisch zum
     * Antworten im MessageCenter). Hier sichtbar gemacht.
     */
    externalEmails() {
      return (this.conversation?.participants || [])
        .map((p) => p.externalEmail)
        .filter(Boolean);
    },

    /** Deep-Link zurück in die Fall-Kommunikation. */
    caseRoute() {
      const conv = this.conversation;
      if (!conv?.idCase) return null;
      return {
        name: "PageCaseDetailNachrichten",
        params: { idCase: String(conv.idCase) },
        query: { conversation: String(conv.id) },
      };
    },
  },

  watch: {
    modelValue(open) {
      if (open) {
        this.resetReply();
        this.loadMessages();
        this.startRefresh();
      } else {
        this.stopRefresh();
        this.messages = [];
      }
    },
    "conversation.id"() {
      if (this.modelValue) this.loadMessages();
    },
  },

  beforeUnmount() {
    this.stopRefresh();
  },

  methods: {
    close() {
      this.show = false;
    },

    startRefresh() {
      this.stopRefresh();
      this.refreshTimer = setInterval(() => this.loadMessages(true), REFRESH_INTERVAL_MS);
    },

    stopRefresh() {
      if (this.refreshTimer) {
        clearInterval(this.refreshTimer);
        this.refreshTimer = null;
      }
    },

    /** Identisch zu MessageCenter.convBase: fallgebunden vs. fallfrei. */
    convBase() {
      const conv = this.conversation;
      if (!conv) return null;
      return conv.idCase
        ? `${API_BASE_URL}/protected/cases/${conv.idCase}/conversations/${conv.id}`
        : `${API_BASE_URL}/protected/messages/conversations/${conv.id}`;
    },

    /**
     * Absender & Richtung speichern (wie MessageCenter.applyClassification):
     * Verlauf neu laden und die Listen der Kommunikation benachrichtigen.
     */
    async applyClassification(payload) {
      const message = this.classifyTarget;
      if (!message || this.classifying) return;
      this.classifying = true;
      try {
        await setMessageDirection(message.id, payload.direction, {
          counterpartEmail: payload.counterpartEmail,
          counterpartName: payload.counterpartName,
          recipients: payload.recipients,
        });
        this.classifyTarget = null;
        useSnackbarStore().showSuccess(
          payload.direction === "in" ? this.$gt("Als eingegangen eingeordnet") : this.$gt("Als gesendet eingeordnet"),
        );
      } catch (error) {
        useSnackbarStore().showError(this.$gt(error.message));
        return;
      } finally {
        this.classifying = false;
      }
      await this.loadMessages(true);
      this.$emit("sent");
    },

    async loadMessages(silent = false) {
      const base = this.convBase();
      if (!base) return;
      if (!silent) this.loading = true;
      try {
        const res = await fetch(`${base}/messages`, { credentials: "include" });
        if (res.status === 401) { useAuthStore().logout(); return; }
        if (res.ok) {
          const hadCount = this.messages.length;
          this.messages = await res.json();
          if (!silent || this.messages.length !== hadCount) {
            this.$nextTick(() => this.scrollToBottom());
          }
        }
      } catch { /* still — Fenster bleibt bedienbar */ } finally {
        if (!silent) this.loading = false;
      }
    },

    /**
     * Versand identisch zu MessageCenter.sendReply: HTML-Inhalt an denselben
     * Konversations-Endpunkt, danach Anhänge hochladen und den Verlauf neu
     * laden. Das Backend stellt die Antwort automatisch per E-Mail an
     * externe Teilnehmer zu.
     */
    async sendReply() {
      const base = this.convBase();
      if (!this.canSend || this.sending || !base) return;
      this.sending = true;
      // Mit Anhängen darf die Mail an Externe erst nach dem Upload raus
      // (deferExternalSend), sonst fehlt der Anhang beim Empfänger
      // (Kundenfeedback 25.09.2026).
      const hasAttachments = this.pendingAttachments.length > 0 || this.pendingDocuments.length > 0;
      try {
        const res = await fetch(`${base}/messages`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          // Nur Anlagen: Das Backend verlangt einen Inhalt – ein leerer Absatz genügt.
          body: JSON.stringify({ content: this.replyHtml || "<p></p>", deferExternalSend: hasAttachments }),
        });
        if (!res.ok) throw new Error("Senden fehlgeschlagen");
        const msg = await res.json();
        this.messages.push(msg);

        if (hasAttachments) {
          try {
            await this.uploadAttachments(msg.id, this.pendingAttachments);
            const failedDocs = await attachCaseDocuments(this.conversation, msg.id, this.pendingDocuments);
            if (failedDocs.length) {
              useSnackbarStore().showError(`Nicht angehängt: ${failedDocs.join(", ")}`);
            }
          } finally {
            await this.dispatchExternals(msg.id);
          }
          const again = await fetch(`${base}/messages`, { credentials: "include" });
          if (again.ok) this.messages = await again.json();
        }

        this.resetReply();
        this.$nextTick(() => this.scrollToBottom());
        this.$emit("sent");
      } catch (error) {
        useSnackbarStore().showError(error.message || "Senden fehlgeschlagen");
      } finally {
        this.sending = false;
      }
    },

    /**
     * Versand an externe Empfänger auslösen, nachdem die Anhänge hochgeladen
     * sind (Gegenstück zu deferExternalSend, wie im MessageCenter).
     */
    async dispatchExternals(messageId) {
      const base = this.convBase();
      if (!base) return;
      try {
        const res = await fetch(
          `${base}/messages/${messageId}/dispatch-externals`,
          { method: "POST", credentials: "include" },
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
      } catch {
        useSnackbarStore().showError("Die E-Mail an die externen Empfänger konnte nicht ausgelöst werden. Bitte im Postausgang prüfen.");
      }
    },

    /**
     * Anhänge zur gesendeten Nachricht hochladen (wie im MessageCenter).
     * Fehlgeschlagene Anhänge werden gemeldet, damit niemand eine Mail ohne
     * den angekündigten Anhang für zugestellt hält.
     */
    async uploadAttachments(messageId, files) {
      const base = this.convBase();
      if (!base) return;
      const failed = [];
      for (const file of files) {
        if (!(file instanceof File)) continue;
        const fd = new FormData();
        fd.append("file", file);
        try {
          const res = await fetch(
            `${base}/messages/${messageId}/attachments`,
            { method: "POST", credentials: "include", body: fd },
          );
          if (!res.ok) failed.push(file.name || "Anhang");
        } catch {
          failed.push(file.name || "Anhang");
        }
      }
      if (failed.length) {
        useSnackbarStore().showError(`Nicht angehängt: ${failed.join(", ")}`);
      }
    },

    /**
     * Antwort leeren und neu vorbelegen (Signatur des gewählten Standorts,
     * Zitat) – gleiche Regeln wie im Antwortfeld der Korrespondenz.
     */
    resetReply() {
      this.replyHtml = "";
      this.pendingAttachments = [];
      this.pendingDocuments = [];
      this.$refs.replyComposer?.reset();
      this.$nextTick(() => this.$refs.replyComposer?.activate());
    },

    scrollToBottom() {
      const el = this.$refs.scrollEl;
      if (el) el.scrollTop = el.scrollHeight;
    },

  },
};
</script>

<style scoped>
.cw-window {
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

/* ── Kopf (Drag-Griff) ── */
.cw-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8fafc;
  cursor: grab;
  flex-shrink: 0;
  user-select: none;
}
.cw-head-icon {
  flex-shrink: 0;
  color: #1a73e8;
}
.cw-head-titles {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
}
.cw-head-subject {
  font-size: 0.88rem;
  font-weight: 650;
  color: #0f172a;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.cw-head-case {
  font-size: 0.72rem;
  color: #64748b;
}
.cw-head-actions {
  display: flex;
  align-items: center;
  gap: 2px;
}
.cw-head-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 6px;
  background: none;
  color: #64748b;
  cursor: pointer;
  transition: background 0.1s, color 0.1s;
}
.cw-head-btn:hover {
  background: rgba(0, 0, 0, 0.06);
  color: #0f172a;
}

/* ── Verlauf ── */
.cw-body {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  background: #fff;
}
.cw-center {
  display: flex;
  justify-content: center;
  padding: 24px 0;
}
.cw-muted {
  color: #94a3b8;
  font-size: 0.82rem;
}
/* ── Antwort (Rich-Text-Composer) ── */
.cw-foot {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8fafc;
}
.cw-ext {
  display: flex;
  align-items: center;
  gap: 4px;
  min-width: 0;
  padding: 4px 10px;
  font-size: 0.72rem;
  color: #64748b;
  border-bottom: 1px solid rgba(15, 23, 42, 0.06);
}
.cw-ext__text {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
}
.cw-composer {
  margin: 6px 8px 8px;
}
/* Kompakte Editor-Höhe im Seitenfenster (Composer bleibt voll nutzbar). */
.cw-foot .cw-composer :deep(.mail-editor) {
  max-height: 180px;
  font-size: 0.82rem;
}
.cw-case-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 0.74rem;
  color: #1a73e8;
  text-decoration: none;
}
.cw-case-link:hover {
  text-decoration: underline;
}
.cw-send {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  border-radius: 8px;
  padding: 6px 12px;
  background: #1a73e8;
  color: #fff;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s ease, opacity 0.15s ease;
}
.cw-send:hover:not(:disabled) {
  background: #1557b0;
}
.cw-send:disabled {
  opacity: 0.5;
  cursor: default;
}

/* ── Resize-Handles an Kanten und Ecken ── */
.cw-resize { position: absolute; z-index: 30; touch-action: none; }
.cw-resize--n { top: 0; left: 14px; right: 14px; height: 6px; cursor: ns-resize; }
.cw-resize--s { bottom: 0; left: 14px; right: 14px; height: 6px; cursor: ns-resize; }
.cw-resize--e { right: 0; top: 14px; bottom: 14px; width: 6px; cursor: ew-resize; }
.cw-resize--w { left: 0; top: 14px; bottom: 14px; width: 6px; cursor: ew-resize; }
.cw-resize--ne { top: 0; right: 0; width: 14px; height: 14px; cursor: nesw-resize; }
.cw-resize--nw { top: 0; left: 0; width: 14px; height: 14px; cursor: nwse-resize; }
.cw-resize--se { bottom: 0; right: 0; width: 14px; height: 14px; cursor: nwse-resize; }
.cw-resize--sw { bottom: 0; left: 0; width: 14px; height: 14px; cursor: nesw-resize; }
</style>
